// Normalizes newsletter sections loaded from a SAVED campaign so the section
// renderers never hit an undefined nested field.
//
// The bug it fixes (M9, reported 2026-06-06: "marketing > email campaign >
// edit → fatal error, nothing works"): the section components assume a fixed
// `content` shape — `company_news` reads `content.items.map(...)` and each
// `item.images.slice(...)`; `introducing` reads `content.left.title` /
// `content.right.title`. A campaign saved with an older / partial
// `content_data.sections` shape (e.g. a company-news section with no `items`,
// or an item with no `images`) made those renderers throw on mount, blanking
// the whole editor. Create mode never hit it because `getDefaultSections()`
// always has the full shape — only EDIT, loading saved data, crashed.
//
// The webinar builder already guards its load with `ensureAllSections`; the
// olive builder did `setSections(initialSections)` raw. This is that missing
// guard, extracted as a pure function so it's trivially unit-tested.

import { headerImageOptions, type NewsletterSection } from "./stock-images";

function asObject(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/** Fill in the per-type nested structure a saved section may be missing, so
 * every downstream renderer can read its fields without an undefined crash.
 * Existing values always win — this only adds missing keys. */
export function normalizeNewsletterSection(section: NewsletterSection): NewsletterSection {
  const content = asObject(section.content);

  switch (section.type) {
    case "company_news": {
      const rawItems = Array.isArray(content.items) ? content.items : [];
      const items = rawItems.map((it) => {
        const item = asObject(it);
        return {
          title: "",
          text: "",
          ...item,
          images: Array.isArray(item.images) ? item.images : [],
        };
      });
      return {
        ...section,
        content: { heading: "Company News", ...content, items },
      };
    }

    case "introducing":
      return {
        ...section,
        content: {
          ...content,
          left: { title: "", text: "", ...asObject(content.left) },
          right: { title: "", text: "", ...asObject(content.right) },
        },
      };

    case "header":
      return {
        ...section,
        content: {
          title: "",
          subtitle: "",
          imageUrl: headerImageOptions[0]?.url ?? "",
          ...content,
        },
      };

    case "case_study":
      // No "Success Story" default heading: when a saved section arrives with
      // no heading (a greeting/intro section, or — the bug — content stripped
      // in transit), the renderer must show NOTHING, not a phantom
      // "Success Story" repeated down the email. Real headings ("The
      // Challenge", …) survive via the `...content` spread.
      return {
        ...section,
        content: { heading: "", title: "", content: "", ...content },
      };

    case "footer":
    default:
      return { ...section, content };
  }
}

/** Normalize a whole saved-section list. Non-array input collapses to `[]`. */
export function normalizeNewsletterSections(
  sections: NewsletterSection[] | null | undefined,
): NewsletterSection[] {
  if (!Array.isArray(sections)) return [];
  return sections.map(normalizeNewsletterSection);
}
